import type {
  Footer,
  Footer_Link,
  Footer_Link_OpenConsentOptionsAction,
  Footer_Section,
  Footer_GroupedSection,
  Footer_ContactInfoEntry,
  Footer_ContactInfoEntry_SocialLink,
  Footer_CertificateLink,
  FooterStatic,
  FooterStatic_ContactInfoEntry,
  FooterStatic_ContactInfoEntry_SocialLink,
  FooterStatic_CertificateLink,
  FooterDynamic,
  FooterLink,
  FooterLink_OpenConsentOptionsAction,
  FooterLinksSection,
  GroupedSection,
  FooterSection,
  FooterSection_Disclaimer,
} from "../../aysa_api/models/footer.types";
import { Reader, Writer } from "protobufjs/minimal";
import { encodeUSP, decodeUSP } from "../../aysa_api/models/usp";
import {
  encodePaymentDeliveryImage,
  decodePaymentDeliveryImage,
} from "../../aysa_api/models/payment_delivery_image";
import {
  createBaseImage,
  encodeImage,
  decodeImage,
} from "../../aysa_api/models/image";
import {
  decodeLinkTarget,
  encodeLinkTarget,
} from "../../aysa_api/models/link_target";
import {
  decodeContactInfo,
  encodeContactInfo,
} from "../../aysa_api/models/contact_info";

export const createBaseFooter = (): Footer => {
  return {
    usps: [],
    paymentDeliveryMethods: [],
    sections: [],
    contactInfo: [],
    serviceLinks: [],
    legalLinks: [],
    copyright: "",
    certificateLinks: [],
    disclaimers: [],

    groupedSections: [],
  };
};

export const decodeFooter = (reader: Reader, length: number): Footer => {
  const end = reader.pos + length;
  const message = createBaseFooter();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.usps.push(decodeUSP(reader, reader.uint32()));

        break;
      case 2:
        message.paymentDeliveryMethods.push(
          decodePaymentDeliveryImage(reader, reader.uint32())
        );

        break;
      case 3:
        message.sections.push(decodeFooter_Section(reader, reader.uint32()));

        break;
      case 4:
        message.contactInfo.push(
          decodeFooter_ContactInfoEntry(reader, reader.uint32())
        );

        break;
      case 5:
        message.serviceLinks.push(decodeFooter_Link(reader, reader.uint32()));

        break;
      case 6:
        message.legalLinks.push(decodeFooter_Link(reader, reader.uint32()));

        break;
      case 7:
        message.copyright = reader.string();

        break;
      case 9:
        message.certificateLinks.push(
          decodeFooter_CertificateLink(reader, reader.uint32())
        );

        break;
      case 10:
        message.disclaimers.push(reader.string());

        break;
      case 11:
        message.seoText = reader.string();

        break;
      case 12:
        message.groupedSections.push(
          decodeFooter_GroupedSection(reader, reader.uint32())
        );

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooter = (writer: Writer, message: Footer): Writer => {
  for (const value of message.usps) {
    encodeUSP(writer.uint32(10).fork(), value).ldelim();
  }
  for (const value of message.paymentDeliveryMethods) {
    encodePaymentDeliveryImage(writer.uint32(18).fork(), value).ldelim();
  }
  for (const value of message.sections) {
    encodeFooter_Section(writer.uint32(26).fork(), value).ldelim();
  }
  for (const value of message.contactInfo) {
    encodeFooter_ContactInfoEntry(writer.uint32(34).fork(), value).ldelim();
  }
  for (const value of message.serviceLinks) {
    encodeFooter_Link(writer.uint32(42).fork(), value).ldelim();
  }
  for (const value of message.legalLinks) {
    encodeFooter_Link(writer.uint32(50).fork(), value).ldelim();
  }
  writer.uint32(58).string(message.copyright);
  for (const value of message.certificateLinks) {
    encodeFooter_CertificateLink(writer.uint32(74).fork(), value).ldelim();
  }
  for (const value of message.disclaimers) {
    writer.uint32(82).string(value);
  }
  if (message.seoText !== undefined) {
    writer.uint32(90).string(message.seoText);
  }
  for (const value of message.groupedSections) {
    encodeFooter_GroupedSection(writer.uint32(98).fork(), value).ldelim();
  }

  return writer;
};

export const createBaseFooter_Link = (): Footer_Link => {
  return {
    label: "",
    deprecatedHref: "",
  };
};

export const decodeFooter_Link = (
  reader: Reader,
  length: number
): Footer_Link => {
  const end = reader.pos + length;
  const message = createBaseFooter_Link();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.label = reader.string();

        break;
      case 2:
        message.deprecatedHref = reader.string();

        break;
      case 3:
        message.action = {
          $case: "href",
          href: decodeLinkTarget(reader, reader.uint32()),
        };

        break;
      case 4:
        message.action = {
          $case: "openConsentOptions",
          openConsentOptions: decodeFooter_Link_OpenConsentOptionsAction(
            reader,
            reader.uint32()
          ),
        };

        break;
      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooter_Link = (
  writer: Writer,
  message: Footer_Link
): Writer => {
  writer.uint32(10).string(message.label);
  writer.uint32(18).string(message.deprecatedHref);
  if (message.action !== undefined) {
    switch (message.action.$case) {
      case "href":
        encodeLinkTarget(
          writer.uint32(26).fork(),
          message.action.href
        ).ldelim();

        break;
      case "openConsentOptions":
        encodeFooter_Link_OpenConsentOptionsAction(
          writer.uint32(34).fork(),
          message.action.openConsentOptions
        ).ldelim();

        break;
    }
  }
  return writer;
};

export const createBaseFooter_Link_OpenConsentOptionsAction =
  (): Footer_Link_OpenConsentOptionsAction => {
    return {};
  };

export const decodeFooter_Link_OpenConsentOptionsAction = (
  reader: Reader,
  length: number
): Footer_Link_OpenConsentOptionsAction => {
  const end = reader.pos + length;
  const message = createBaseFooter_Link_OpenConsentOptionsAction();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooter_Link_OpenConsentOptionsAction = (
  writer: Writer,
  _message: Footer_Link_OpenConsentOptionsAction
): Writer => {
  return writer;
};

export const createBaseFooter_Section = (): Footer_Section => {
  return {
    title: "",
    links: [],
  };
};

export const decodeFooter_Section = (
  reader: Reader,
  length: number
): Footer_Section => {
  const end = reader.pos + length;
  const message = createBaseFooter_Section();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.title = reader.string();

        break;
      case 2:
        message.links.push(decodeFooter_Link(reader, reader.uint32()));

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooter_Section = (
  writer: Writer,
  message: Footer_Section
): Writer => {
  writer.uint32(10).string(message.title);
  for (const value of message.links) {
    encodeFooter_Link(writer.uint32(18).fork(), value).ldelim();
  }

  return writer;
};

export const createBaseFooter_GroupedSection = (): Footer_GroupedSection => {
  return {
    title: createBaseFooter_Link(),
    links: [],
  };
};

export const decodeFooter_GroupedSection = (
  reader: Reader,
  length: number
): Footer_GroupedSection => {
  const end = reader.pos + length;
  const message = createBaseFooter_GroupedSection();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.title = decodeFooter_Link(reader, reader.uint32());

        break;
      case 2:
        message.links.push(decodeFooter_Link(reader, reader.uint32()));

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooter_GroupedSection = (
  writer: Writer,
  message: Footer_GroupedSection
): Writer => {
  encodeFooter_Link(writer.uint32(10).fork(), message.title).ldelim();
  for (const value of message.links) {
    encodeFooter_Link(writer.uint32(18).fork(), value).ldelim();
  }

  return writer;
};

export const createBaseFooter_ContactInfoEntry =
  (): Footer_ContactInfoEntry => {
    return {};
  };

export const decodeFooter_ContactInfoEntry = (
  reader: Reader,
  length: number
): Footer_ContactInfoEntry => {
  const end = reader.pos + length;
  const message = createBaseFooter_ContactInfoEntry();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.type = {
          $case: "socialLink",
          socialLink: decodeFooter_ContactInfoEntry_SocialLink(
            reader,
            reader.uint32()
          ),
        };

        break;
      case 5:
        message.type = {
          $case: "phone",
          phone: decodeContactInfo(reader, reader.uint32()),
        };

        break;
      case 6:
        message.type = {
          $case: "vipWhatsappPhone",
          vipWhatsappPhone: decodeContactInfo(reader, reader.uint32()),
        };

        break;
      case 7:
        message.type = {
          $case: "chat",
          chat: decodeContactInfo(reader, reader.uint32()),
        };

        break;
      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooter_ContactInfoEntry = (
  writer: Writer,
  message: Footer_ContactInfoEntry
): Writer => {
  if (message.type !== undefined) {
    switch (message.type.$case) {
      case "socialLink":
        encodeFooter_ContactInfoEntry_SocialLink(
          writer.uint32(10).fork(),
          message.type.socialLink
        ).ldelim();

        break;
      case "phone":
        encodeContactInfo(
          writer.uint32(42).fork(),
          message.type.phone
        ).ldelim();

        break;
      case "vipWhatsappPhone":
        encodeContactInfo(
          writer.uint32(50).fork(),
          message.type.vipWhatsappPhone
        ).ldelim();

        break;
      case "chat":
        encodeContactInfo(writer.uint32(58).fork(), message.type.chat).ldelim();

        break;
    }
  }
  return writer;
};

export const createBaseFooter_ContactInfoEntry_SocialLink =
  (): Footer_ContactInfoEntry_SocialLink => {
    return {
      image: createBaseImage(),
      href: "",
    };
  };

export const decodeFooter_ContactInfoEntry_SocialLink = (
  reader: Reader,
  length: number
): Footer_ContactInfoEntry_SocialLink => {
  const end = reader.pos + length;
  const message = createBaseFooter_ContactInfoEntry_SocialLink();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.image = decodeImage(reader, reader.uint32());

        break;
      case 2:
        message.href = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooter_ContactInfoEntry_SocialLink = (
  writer: Writer,
  message: Footer_ContactInfoEntry_SocialLink
): Writer => {
  encodeImage(writer.uint32(10).fork(), message.image).ldelim();
  writer.uint32(18).string(message.href);

  return writer;
};

export const createBaseFooter_CertificateLink = (): Footer_CertificateLink => {
  return {
    label: "",
    href: "",
    image: createBaseImage(),
  };
};

export const decodeFooter_CertificateLink = (
  reader: Reader,
  length: number
): Footer_CertificateLink => {
  const end = reader.pos + length;
  const message = createBaseFooter_CertificateLink();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.label = reader.string();

        break;
      case 2:
        message.href = reader.string();

        break;
      case 3:
        message.image = decodeImage(reader, reader.uint32());

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooter_CertificateLink = (
  writer: Writer,
  message: Footer_CertificateLink
): Writer => {
  writer.uint32(10).string(message.label);
  writer.uint32(18).string(message.href);
  encodeImage(writer.uint32(26).fork(), message.image).ldelim();

  return writer;
};

export const createBaseFooterStatic = (): FooterStatic => {
  return {
    usps: [],
    paymentDeliveryMethods: [],
    linksSections: [],
    contactInfo: [],
    serviceLinks: [],
    legalLinks: [],
    copyright: "",
    certificateLinks: [],
    disclaimers: [],
    groupedSections: [],
  };
};

export const decodeFooterStatic = (
  reader: Reader,
  length: number
): FooterStatic => {
  const end = reader.pos + length;
  const message = createBaseFooterStatic();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.usps.push(decodeUSP(reader, reader.uint32()));

        break;
      case 2:
        message.paymentDeliveryMethods.push(
          decodePaymentDeliveryImage(reader, reader.uint32())
        );

        break;
      case 3:
        message.linksSections.push(
          decodeFooterLinksSection(reader, reader.uint32())
        );

        break;
      case 4:
        message.contactInfo.push(
          decodeFooterStatic_ContactInfoEntry(reader, reader.uint32())
        );

        break;
      case 5:
        message.serviceLinks.push(decodeFooterLink(reader, reader.uint32()));

        break;
      case 6:
        message.legalLinks.push(decodeFooterLink(reader, reader.uint32()));

        break;
      case 7:
        message.copyright = reader.string();

        break;
      case 8:
        message.certificateLinks.push(
          decodeFooterStatic_CertificateLink(reader, reader.uint32())
        );

        break;
      case 9:
        message.disclaimers.push(reader.string());

        break;
      case 10:
        message.groupedSections.push(
          decodeGroupedSection(reader, reader.uint32())
        );

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterStatic = (
  writer: Writer,
  message: FooterStatic
): Writer => {
  for (const value of message.usps) {
    encodeUSP(writer.uint32(10).fork(), value).ldelim();
  }
  for (const value of message.paymentDeliveryMethods) {
    encodePaymentDeliveryImage(writer.uint32(18).fork(), value).ldelim();
  }
  for (const value of message.linksSections) {
    encodeFooterLinksSection(writer.uint32(26).fork(), value).ldelim();
  }
  for (const value of message.contactInfo) {
    encodeFooterStatic_ContactInfoEntry(
      writer.uint32(34).fork(),
      value
    ).ldelim();
  }
  for (const value of message.serviceLinks) {
    encodeFooterLink(writer.uint32(42).fork(), value).ldelim();
  }
  for (const value of message.legalLinks) {
    encodeFooterLink(writer.uint32(50).fork(), value).ldelim();
  }
  writer.uint32(58).string(message.copyright);
  for (const value of message.certificateLinks) {
    encodeFooterStatic_CertificateLink(
      writer.uint32(66).fork(),
      value
    ).ldelim();
  }
  for (const value of message.disclaimers) {
    writer.uint32(74).string(value);
  }
  for (const value of message.groupedSections) {
    encodeGroupedSection(writer.uint32(82).fork(), value).ldelim();
  }

  return writer;
};

export const createBaseFooterStatic_ContactInfoEntry =
  (): FooterStatic_ContactInfoEntry => {
    return {};
  };

export const decodeFooterStatic_ContactInfoEntry = (
  reader: Reader,
  length: number
): FooterStatic_ContactInfoEntry => {
  const end = reader.pos + length;
  const message = createBaseFooterStatic_ContactInfoEntry();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.type = {
          $case: "socialLink",
          socialLink: decodeFooterStatic_ContactInfoEntry_SocialLink(
            reader,
            reader.uint32()
          ),
        };

        break;
      case 2:
        message.type = {
          $case: "phone",
          phone: decodeContactInfo(reader, reader.uint32()),
        };

        break;
      case 3:
        message.type = {
          $case: "vipWhatsappPhone",
          vipWhatsappPhone: decodeContactInfo(reader, reader.uint32()),
        };

        break;
      case 4:
        message.type = {
          $case: "chat",
          chat: decodeContactInfo(reader, reader.uint32()),
        };

        break;
      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterStatic_ContactInfoEntry = (
  writer: Writer,
  message: FooterStatic_ContactInfoEntry
): Writer => {
  if (message.type !== undefined) {
    switch (message.type.$case) {
      case "socialLink":
        encodeFooterStatic_ContactInfoEntry_SocialLink(
          writer.uint32(10).fork(),
          message.type.socialLink
        ).ldelim();

        break;
      case "phone":
        encodeContactInfo(
          writer.uint32(18).fork(),
          message.type.phone
        ).ldelim();

        break;
      case "vipWhatsappPhone":
        encodeContactInfo(
          writer.uint32(26).fork(),
          message.type.vipWhatsappPhone
        ).ldelim();

        break;
      case "chat":
        encodeContactInfo(writer.uint32(34).fork(), message.type.chat).ldelim();

        break;
    }
  }
  return writer;
};

export const createBaseFooterStatic_ContactInfoEntry_SocialLink =
  (): FooterStatic_ContactInfoEntry_SocialLink => {
    return {
      image: createBaseImage(),
      href: "",
    };
  };

export const decodeFooterStatic_ContactInfoEntry_SocialLink = (
  reader: Reader,
  length: number
): FooterStatic_ContactInfoEntry_SocialLink => {
  const end = reader.pos + length;
  const message = createBaseFooterStatic_ContactInfoEntry_SocialLink();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.image = decodeImage(reader, reader.uint32());

        break;
      case 2:
        message.href = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterStatic_ContactInfoEntry_SocialLink = (
  writer: Writer,
  message: FooterStatic_ContactInfoEntry_SocialLink
): Writer => {
  encodeImage(writer.uint32(10).fork(), message.image).ldelim();
  writer.uint32(18).string(message.href);

  return writer;
};

export const createBaseFooterStatic_CertificateLink =
  (): FooterStatic_CertificateLink => {
    return {
      label: "",
      href: "",
      image: createBaseImage(),
    };
  };

export const decodeFooterStatic_CertificateLink = (
  reader: Reader,
  length: number
): FooterStatic_CertificateLink => {
  const end = reader.pos + length;
  const message = createBaseFooterStatic_CertificateLink();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.label = reader.string();

        break;
      case 2:
        message.href = reader.string();

        break;
      case 3:
        message.image = decodeImage(reader, reader.uint32());

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterStatic_CertificateLink = (
  writer: Writer,
  message: FooterStatic_CertificateLink
): Writer => {
  writer.uint32(10).string(message.label);
  writer.uint32(18).string(message.href);
  encodeImage(writer.uint32(26).fork(), message.image).ldelim();

  return writer;
};

export const createBaseFooterDynamic = (): FooterDynamic => {
  return {
    linksSections: [],
  };
};

export const decodeFooterDynamic = (
  reader: Reader,
  length: number
): FooterDynamic => {
  const end = reader.pos + length;
  const message = createBaseFooterDynamic();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.seoText = reader.string();

        break;
      case 2:
        message.linksSections.push(
          decodeFooterLinksSection(reader, reader.uint32())
        );

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterDynamic = (
  writer: Writer,
  message: FooterDynamic
): Writer => {
  if (message.seoText !== undefined) {
    writer.uint32(10).string(message.seoText);
  }
  for (const value of message.linksSections) {
    encodeFooterLinksSection(writer.uint32(18).fork(), value).ldelim();
  }

  return writer;
};

export const createBaseFooterLink = (): FooterLink => {
  return {
    label: "",
  };
};

export const decodeFooterLink = (
  reader: Reader,
  length: number
): FooterLink => {
  const end = reader.pos + length;
  const message = createBaseFooterLink();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.label = reader.string();

        break;
      case 3:
        message.action = {
          $case: "href",
          href: decodeLinkTarget(reader, reader.uint32()),
        };

        break;
      case 4:
        message.action = {
          $case: "openConsentOptions",
          openConsentOptions: decodeFooterLink_OpenConsentOptionsAction(
            reader,
            reader.uint32()
          ),
        };

        break;
      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterLink = (
  writer: Writer,
  message: FooterLink
): Writer => {
  writer.uint32(10).string(message.label);
  if (message.action !== undefined) {
    switch (message.action.$case) {
      case "href":
        encodeLinkTarget(
          writer.uint32(26).fork(),
          message.action.href
        ).ldelim();

        break;
      case "openConsentOptions":
        encodeFooterLink_OpenConsentOptionsAction(
          writer.uint32(34).fork(),
          message.action.openConsentOptions
        ).ldelim();

        break;
    }
  }
  return writer;
};

export const createBaseFooterLink_OpenConsentOptionsAction =
  (): FooterLink_OpenConsentOptionsAction => {
    return {};
  };

export const decodeFooterLink_OpenConsentOptionsAction = (
  reader: Reader,
  length: number
): FooterLink_OpenConsentOptionsAction => {
  const end = reader.pos + length;
  const message = createBaseFooterLink_OpenConsentOptionsAction();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterLink_OpenConsentOptionsAction = (
  writer: Writer,
  _message: FooterLink_OpenConsentOptionsAction
): Writer => {
  return writer;
};

export const createBaseFooterLinksSection = (): FooterLinksSection => {
  return {
    title: "",
    links: [],
  };
};

export const decodeFooterLinksSection = (
  reader: Reader,
  length: number
): FooterLinksSection => {
  const end = reader.pos + length;
  const message = createBaseFooterLinksSection();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.title = reader.string();

        break;
      case 2:
        message.links.push(decodeFooterLink(reader, reader.uint32()));

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterLinksSection = (
  writer: Writer,
  message: FooterLinksSection
): Writer => {
  writer.uint32(10).string(message.title);
  for (const value of message.links) {
    encodeFooterLink(writer.uint32(18).fork(), value).ldelim();
  }

  return writer;
};

export const createBaseGroupedSection = (): GroupedSection => {
  return {
    title: createBaseFooterLink(),
    links: [],
  };
};

export const decodeGroupedSection = (
  reader: Reader,
  length: number
): GroupedSection => {
  const end = reader.pos + length;
  const message = createBaseGroupedSection();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.title = decodeFooterLink(reader, reader.uint32());

        break;
      case 2:
        message.links.push(decodeFooterLink(reader, reader.uint32()));

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeGroupedSection = (
  writer: Writer,
  message: GroupedSection
): Writer => {
  encodeFooterLink(writer.uint32(10).fork(), message.title).ldelim();
  for (const value of message.links) {
    encodeFooterLink(writer.uint32(18).fork(), value).ldelim();
  }

  return writer;
};

export const createBaseFooterSection = (): FooterSection => {
  return {
    disclaimers: [],
  };
};

export const decodeFooterSection = (
  reader: Reader,
  length: number
): FooterSection => {
  const end = reader.pos + length;
  const message = createBaseFooterSection();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.disclaimers.push(
          decodeFooterSection_Disclaimer(reader, reader.uint32())
        );

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterSection = (
  writer: Writer,
  message: FooterSection
): Writer => {
  for (const value of message.disclaimers) {
    encodeFooterSection_Disclaimer(writer.uint32(10).fork(), value).ldelim();
  }

  return writer;
};

export const createBaseFooterSection_Disclaimer =
  (): FooterSection_Disclaimer => {
    return {
      label: "",
    };
  };

export const decodeFooterSection_Disclaimer = (
  reader: Reader,
  length: number
): FooterSection_Disclaimer => {
  const end = reader.pos + length;
  const message = createBaseFooterSection_Disclaimer();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.label = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeFooterSection_Disclaimer = (
  writer: Writer,
  message: FooterSection_Disclaimer
): Writer => {
  writer.uint32(10).string(message.label);

  return writer;
};
